import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";

const StandardClause = ({ n, std, ov, readOnly, onToggle, onEdit }) => (
  <div className={`rounded-md border p-3 ${ov ? "border-[#E0B84B] bg-[#FFFBEB]" : "border-border bg-white"}`} data-testid={`terms-clause-${std.key}`}>
    <label className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
      <Checkbox checked={!!ov} disabled={readOnly} onCheckedChange={(v) => onToggle(!!v)} data-testid={`terms-override-${std.key}`} />
      {n}. {std.heading}
      <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{ov ? "OEM Wording" : "Standard Somcore"}</span>
    </label>
    {ov ? (
      <Textarea rows={3} className="mt-2 bg-white text-xs" readOnly={readOnly} value={ov.text} onChange={(e) => onEdit(e.target.value)} data-testid={`terms-text-${std.key}`} />
    ) : (
      <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{std.text.replace("{cur}", "TZS/USD")}</p>
    )}
  </div>
);

export const TermsEditor = ({ standard, value, onChange, readOnly = false }) => {
  const terms = value || [];
  const stdKeys = standard.map((s) => s.key);
  const extras = terms.filter((t) => !stdKeys.includes(t.key));
  const find = (k) => terms.find((t) => t.key === k);
  const upsert = (k, patch) => onChange(terms.some((t) => t.key === k) ? terms.map((t) => (t.key === k ? { ...t, ...patch } : t)) : [...terms, { key: k, ...patch }]);
  const remove = (k) => onChange(terms.filter((t) => t.key !== k));
  const toggle = (s, on) => (on ? upsert(s.key, { heading: s.heading, text: s.text.replace("{cur}", "TZS") }) : remove(s.key));
  const addExtra = () => onChange([...terms, { key: `x${Date.now()}`, heading: "", text: "" }]);

  return (
    <div className="space-y-2" data-testid="terms-editor">
      {standard.map((s, i) => (
        <StandardClause key={s.key} n={i + 1} std={s} ov={find(s.key)} readOnly={readOnly}
          onToggle={(on) => toggle(s, on)} onEdit={(text) => upsert(s.key, { text })} />
      ))}
      {extras.map((t, i) => (
        <div key={t.key} className="rounded-md border border-[#E0B84B] bg-[#FFFBEB] p-3" data-testid={`terms-extra-${i}`}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#1B2A4A]">{standard.length + i + 1}.</span>
            <Input className="h-8 bg-white" placeholder="Clause Heading, e.g. Warranty & Genuine Parts" readOnly={readOnly}
              value={t.heading} onChange={(e) => upsert(t.key, { heading: e.target.value })} data-testid={`terms-extra-heading-${i}`} />
            {!readOnly && <Button type="button" variant="ghost" size="sm" onClick={() => remove(t.key)} data-testid={`terms-extra-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
          </div>
          <Textarea rows={3} className="mt-2 bg-white text-xs" readOnly={readOnly} value={t.text}
            onChange={(e) => upsert(t.key, { text: e.target.value })} data-testid={`terms-extra-text-${i}`} />
        </div>
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={addExtra} data-testid="terms-add-extra">
          <Plus className="mr-1.5 h-4 w-4" />Add OEM Clause
        </Button>
      )}
    </div>
  );
};

export default TermsEditor;
